import React, { useState } from 'react';
import { ShieldCheck, Check } from 'lucide-react';
import { StepNavButtons } from './StepNavButtons';
import { MarkdownContent } from '../MarkdownContent';
import type { ModuleStep } from '@/lib/models/module';

interface ConfirmationStepRendererProps {
    step: ModuleStep;
    onSubmit: (answer: any) => void;
    isSubmitting: boolean;
    canGoBack: boolean;
    onGoBack: () => void;
}

export const ConfirmationStepRenderer: React.FC<ConfirmationStepRendererProps> = ({ step, onSubmit, isSubmitting, canGoBack, onGoBack }) => {
    const { heading, body, checkbox_label } = step.content;
    const [confirmed, setConfirmed] = useState(false);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900">{heading || step.step_title}</h2>
                </div>
            </div>

            {/* Body */}
            {body && <MarkdownContent>{body}</MarkdownContent>}

            {/* Checkbox */}
            <label className={`group flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${confirmed ? 'border-[#1A4D4F] bg-[#A4C3B2]/20 shadow-sm' : 'border-gray-200 hover:border-[#1A4D4F]/40 hover:bg-gray-50'}`}>
                <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="sr-only"
                />
                <span
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                        confirmed ? 'border-[#1A4D4F] bg-[#1A4D4F]' : 'border-gray-300 group-hover:border-[#1A4D4F]/50'
                    }`}
                >
                    {confirmed && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </span>
                <span className="font-medium text-gray-900">{checkbox_label}</span>
            </label>

            {/* Navigation */}
            <StepNavButtons
                canGoBack={canGoBack}
                onGoBack={onGoBack}
                onNext={() => onSubmit({ confirmed: true })}
                nextLabel={isSubmitting ? 'Laddar...' : 'Bekräfta & Fortsätt'}
                nextDisabled={!confirmed || isSubmitting}
            />
        </div>
    );
};
